/**
 * Page de retour Stripe après paiement réussi.
 * - Récupère session_id depuis l'URL
 * - Poll /api/stripe/checkout/status jusqu'à confirmation ou timeout
 * - Affiche l'état au client
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/api";
import { CheckCircle2, Loader2, Clock } from "lucide-react";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL = 2500;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState("polling");
  const [data, setData] = useState(null);
  const attemptsRef = useRef(0);

  const poll = useCallback(async () => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    try {
      const r = await api.get(`/stripe/checkout/status/${sessionId}`);
      setData(r.data);
      if (r.data.payment_status === "paid") {
        setStatus("success");
        return;
      }
      if (r.data.status === "expired") {
        setStatus("expired");
        return;
      }
      attemptsRef.current += 1;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, POLL_INTERVAL);
    } catch {
      setStatus("error");
    }
  }, [sessionId]);

  useEffect(() => { poll(); }, [poll]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-6" data-testid="payment-success-page">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center space-y-4">
        {status === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-blue-500 mx-auto animate-spin" />
            <h1 className="text-xl font-bold text-slate-800">Vérification du paiement...</h1>
            <p className="text-sm text-slate-500">Confirmation en cours auprès de Stripe, patientez quelques secondes.</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto" data-testid="payment-success-icon" />
            <h1 className="text-2xl font-bold text-slate-800">Paiement confirmé</h1>
            <p className="text-sm text-slate-600">
              Merci ! Votre paiement de <strong>{data ? (data.amount_total / 100).toFixed(2) : ""} $ CAD</strong> a bien été reçu.
            </p>
            <p className="text-xs text-slate-400">Un reçu Stripe vous a été envoyé par courriel.</p>
            <button
              onClick={() => navigate(-2)}
              className="mt-4 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 text-sm"
              data-testid="payment-success-back-btn"
            >
              Retour
            </button>
          </>
        )}
        {status === "expired" && (
          <>
            <Clock className="w-12 h-12 text-orange-500 mx-auto" />
            <h1 className="text-xl font-bold text-slate-800">Session expirée</h1>
            <p className="text-sm text-slate-500">La session de paiement a expiré. Veuillez réessayer.</p>
            <button onClick={() => navigate(-2)} className="bg-slate-700 text-white px-5 py-2 rounded-lg text-sm">Retour</button>
          </>
        )}
        {status === "timeout" && (
          <>
            <Clock className="w-12 h-12 text-orange-500 mx-auto" />
            <h1 className="text-xl font-bold text-slate-800">Confirmation retardée</h1>
            <p className="text-sm text-slate-500">Stripe n'a pas encore confirmé. Cela peut prendre quelques minutes. Vous recevrez une notification dès réception.</p>
            <button onClick={() => navigate(-2)} className="bg-slate-700 text-white px-5 py-2 rounded-lg text-sm">Retour</button>
          </>
        )}
        {status === "error" && (
          <>
            <Clock className="w-12 h-12 text-orange-500 mx-auto" />
            <h1 className="text-xl font-bold text-slate-800">Confirmation en cours</h1>
            <p className="text-sm text-slate-500">
              Si votre paiement a été accepté par Stripe, votre facture sera mise à jour automatiquement dans les prochaines minutes.
              Vous recevrez un reçu Stripe par courriel.
            </p>
            <button onClick={() => navigate(-2)} className="bg-slate-700 text-white px-5 py-2 rounded-lg text-sm">Retour</button>
          </>
        )}
      </div>
    </div>
  );
}
